import {
	WEBAPP_SYNC_SOURCE,
	SYNC_STORAGE_KEY,
	WEBAPP_SYNC_TYPE,
	FIRST_CONNECTION_SSO_DATA,
} from "../const/sync.const"
import { normalizeBoolean } from "./normalizeBoolean"

/**
 * Récupère une valeur du localStorage de manière sécurisée.
 *
 * @param {string} key - La clé à récupérer.
 * @returns {string|null} La valeur ou null en cas d'erreur.
 */
const safeGetLocalStorageItem = (key) => {
	try {
		return localStorage.getItem(key)
	} catch {
		return null
	}
}

/**
 * Parse une chaîne JSON de manière sécurisée.
 *
 * @param {string|null} jsonString - La chaîne JSON à parser.
 * @returns {Object|null} L'objet parsé ou null en cas d'erreur.
 */
const safeJsonParse = (jsonString) => {
	if (!jsonString || typeof jsonString !== "string") {
		return null
	}
	try {
		return JSON.parse(jsonString)
	} catch {
		return null
	}
}

/**
 * Extrait les données JWT et session d'un token parsé.
 *
 * @param {Object|null} tokenParsed - Le token parsé.
 * @returns {{ jwt: string|null, sessionData: Object|null }} Les données extraites.
 */
const extractTokenData = (tokenParsed) => {
	if (!tokenParsed || typeof tokenParsed !== "object") {
		return { jwt: null, sessionData: null }
	}
	return {
		jwt: tokenParsed.token ?? null,
		sessionData: tokenParsed.userSession ?? null,
	}
}

/**
 * Synchronise les données de la webapp avec l'extension V4.
 *
 * Récupère les données du localStorage, construit un payload et l'envoie
 * via postMessage et sessionStorage.
 */
export const syncWithExtensionV4 = () => {
	try {
		const token = safeGetLocalStorageItem("token")
		const apiUrl = safeGetLocalStorageItem("apiUrl")
		const userEmail = safeGetLocalStorageItem("userEmail")
		const xlsToken = safeGetLocalStorageItem("xLsToken")
		const canSendSms = safeGetLocalStorageItem("canSendSms")
		const v3ApiVersion = safeGetLocalStorageItem("apiVersion")
		const mfaLogged = safeGetLocalStorageItem("mfaLogged")
		const language = safeGetLocalStorageItem("i18nextLng")
		const darkMode = safeGetLocalStorageItem("LS_darkMode")
		const parsedDarkMode = safeJsonParse(darkMode)

		const theme = parsedDarkMode ? "theme-dark" : "theme-light"

		const tokenParsed = safeJsonParse(token)
		const { jwt, sessionData } = extractTokenData(tokenParsed)

		// relative to first sso connection
		const v3FirstConnectionSsoData = safeJsonParse(
			safeGetLocalStorageItem(FIRST_CONNECTION_SSO_DATA),
			"Failed to parse sso login informations from localStorage",
		)

		const payload = {
			apiUrl: apiUrl ?? null,
			userEmail: userEmail ?? null,
			jwt,
			xlsToken: xlsToken ?? null,
			mfaLogged: normalizeBoolean(mfaLogged),
			sessionData,
			canSendSms: normalizeBoolean(canSendSms),
			v3ApiVersion: v3ApiVersion ?? null,
			language: language ?? null,
			v3FirstConnectionSsoData: v3FirstConnectionSsoData ?? null,
			theme,
		}

		try {
			sessionStorage.setItem(SYNC_STORAGE_KEY, JSON.stringify(payload))
		} catch {}

		if (typeof window !== "undefined" && window.postMessage) {
			window.postMessage(
				{
					source: WEBAPP_SYNC_SOURCE,
					type: WEBAPP_SYNC_TYPE,
					payload: { ...payload, version: "v3" },
				},
				window.location.origin,
			)
		}
	} catch {}
}
